import { createFileRoute, Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronRight, Crown, Gamepad2, LayoutGrid, Scale } from "lucide-react";

import { memo, useEffect, useState } from "react";

// Import your existing UI components
import type { Rank } from "../../components/ui";
import { Button, RankIcon, rankColors, rankVar } from "../../components/ui";

export const Route = createFileRoute("/(public)/")({
  component: LandingPage,
});

// --- 1. HERO BACKGROUND: The Arena Floor ---
const HeroBackground = memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
    {/* Layered radial gradients for depth */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_60%_at_50%_50%,rgba(31,251,245,0.04),transparent_70%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(31,251,240,0.04),transparent_40%)]" />

    {/* Floating particles with varied sizes */}
    <div
      className="absolute top-1/4 left-1/4 w-1 h-1 bg-brand-400/40 rounded-full animate-float"
      style={{ animationDelay: "0s", animationDuration: "3s" }}
    />
    <div
      className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-brand-400/30 rounded-full animate-float"
      style={{ animationDelay: "1s", animationDuration: "4s" }}
    />
    <div
      className="absolute bottom-1/3 left-1/2 w-1 h-1 bg-brand-400/50 rounded-full animate-float"
      style={{ animationDelay: "2s", animationDuration: "5s" }}
    />
    <div
      className="absolute top-1/2 right-1/4 w-2 h-2 bg-brand-400/20 rounded-full animate-float"
      style={{ animationDelay: "1.5s", animationDuration: "6s" }}
    />
    <div
      className="absolute bottom-1/4 right-1/2 w-1 h-1 bg-brand-400/40 rounded-full animate-float"
      style={{ animationDelay: "0.5s", animationDuration: "4.5s" }}
    />
    <div
      className="absolute top-3/4 left-1/3 w-1.5 h-1.5 bg-brand-400/35 rounded-full animate-float"
      style={{ animationDelay: "2.5s", animationDuration: "5.5s" }}
    />

    {/* Subtle grid overlay */}
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(31,251,221,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(31,251,221,0.2) 1px, transparent 1px)",
        backgroundSize: "100px 100px",
      }}
    />
  </div>
));

// --- 3. RANK BACKGROUND: Stage Spotlight ---
const RankBackground = memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none select-none bg-gradient-to-b from-surface-900 via-surface-950 to-surface-900">
    {/* Center spotlight - radial light from behind/above */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(31,251,221,0.08),transparent_50%)]" />

    {/* Secondary accent spotlight from bottom */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_50%_100%,rgba(6,182,212,0.06),transparent_50%)]" />

    {/* Dramatic side rim lighting */}
    <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-brand-500/5 to-transparent" />
    <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-cyan-500/5 to-transparent" />

    {/* Subtle grid for structure */}
    <div
      className="absolute inset-0 opacity-[0.015]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(31,251,221,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(31,251,221,0.3) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }}
    />

    {/* Noise texture for depth */}
    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] brightness-200" />
  </div>
));

function RankSystem() {
  return (
    <section className="relative py-28 overflow-hidden bg-surface-950">
      <RankBackground />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="px-6 mb-16 text-center">
          <SectionHeader title="CLIMB TO THE TOP" subtitle="Rank Progression" />
          <p className="text-surface-400 text-lg max-w-xl mx-auto mt-4">
            Seven ranks. Four divisions. One goal.
          </p>
        </div>

        {/* RANK DISPLAY - Mobile: Vertical Stack, Desktop: Horizontal */}
        <div className="relative w-full px-6">
          <div className="flex flex-col gap-8 items-center md:flex-row md:flex-wrap md:justify-center md:gap-4">
            {[
              { id: "bronze" as Rank },
              { id: "silver" as Rank },
              { id: "gold" as Rank },
              { id: "platinum" as Rank },
              { id: "diamond" as Rank },
              { id: "prime" as Rank, featured: true },
              { id: "champion" as Rank, featured: true },
            ].map((rank, i) => (
              <RankFloater
                key={rank.id}
                rank={rank.id}
                name={rankColors[rank.id].name}
                color={rankVar(rank.id)}
                delay={i}
                featured={"featured" in rank ? rank.featured : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function RankFloater({
  rank,
  name,
  color,
  delay,
  featured,
}: {
  rank: Rank;
  name: string;
  color: string;
  delay: number;
  featured?: boolean;
}) {
  const hasDivisions = rank !== "prime" && rank !== "champion";
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <motion.div
      initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        delay: isMobile ? 0 : delay * 0.08,
        duration: isMobile ? 0 : 0.4,
      }}
      className="relative group cursor-default flex flex-col items-center justify-center w-[140px] lg:w-[160px]"
    >
      {/* Floating Icon */}
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay * 0.5,
        }}
        className="relative z-10 mb-1"
      >
        <div className="transform transition-all duration-300 group-hover:scale-110">
          <div className="drop-shadow-2xl transition-all duration-300 group-hover:drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
            <RankIcon rank={rank} size={featured ? "xxl" : "xxl"} showLevels={false} />
          </div>
        </div>
      </motion.div>

      {/* Text + Levels */}
      <div className="flex flex-col items-center gap-1.5">
        <h3
          className="font-display font-bold tracking-widest text-base md:text-sm transition-all duration-300 text-white"
          style={
            featured
              ? {
                  textShadow: `0 0 30px ${color}, 0 0 15px ${color}, 0 0 8px ${color}`,
                }
              : undefined
          }
        >
          {name.toUpperCase()}
        </h3>

        {hasDivisions ? (
          <div className="flex items-center gap-1.5 md:gap-1">
            {[...Array(4)].map((_, i) => (
              <img
                key={i}
                src={`/rank_icons/rank_${rank}_level.png`}
                alt={`${name} Level`}
                className="h-6 md:h-4 w-auto opacity-90 transition-all duration-300 group-hover:opacity-100 group-hover:brightness-125"
              />
            ))}
          </div>
        ) : (
          <span
            className="text-xs md:text-[10px] font-mono font-bold text-white"
            style={{
              textShadow: `0 0 25px ${color}, 0 0 12px ${color}, 0 0 6px ${color}`,
            }}
          >
            {rank === "prime" ? "TOP 500" : "TOP 250"}
          </span>
        )}
      </div>
    </motion.div>
  );
}

// --- 4. GAMES BACKGROUND: Game Library ---
const GamesBackground = memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
    {/* Diagonal split gradient from cyan to brand */}
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-brand-500/5" />

    {/* Accent orbs matching game theme - responsive positioning */}
    <div className="absolute left-1/2 -translate-x-1/2 top-0 md:left-1/4 md:top-1/3 md:translate-x-0 w-[250px] h-[250px] md:w-[350px] md:h-[350px] bg-cyan-500/8 blur-[90px] rounded-full" />
    <div className="absolute left-1/2 -translate-x-1/2 bottom-0 md:left-auto md:right-1/4 md:bottom-1/3 md:translate-x-0 w-[250px] h-[250px] md:w-[350px] md:h-[350px] bg-brand-500/8 blur-[90px] rounded-full" />

    {/* Subtle dot pattern */}
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage: "radial-gradient(circle, rgba(31,251,221,0.5) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }}
    />
  </div>
));

function LandingPage() {
  return (
    <div className="relative min-h-screen bg-surface-950 font-sans selection:bg-brand-500/30 selection:text-brand-200 overflow-x-hidden">
      <main className="relative z-10 flex flex-col">
        {/* Sections are now relative containers for their specific backgrounds */}
        <HeroSection />
        <ValuePropSection />
        <RankSystem />
        <SupportedGames />
        <FinalCTA />
      </main>
    </div>
  );
}

// --- SECTIONS ---

function HeroSection() {
  const [showScroll, setShowScroll] = useState(true);

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Specific Background */}
      <HeroBackground />

      <div className="relative max-w-5xl mx-auto text-center z-10 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-500/30 bg-brand-500/5 backdrop-blur-sm mb-8"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
          </span>
          <span className="text-xs font-mono font-bold text-brand-300 tracking-[0.2em] uppercase">
            BETA IS LIVE
          </span>
        </motion.div>

        <div className="relative mb-6">
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-display font-black tracking-tighter leading-[0.85] text-white">
            <span className="block text-surface-500 text-4xl md:text-6xl mb-2 tracking-normal font-display font-bold">
              YOUR COMPETITIVE
            </span>
            <span className="relative inline-block">
              {/* Animated underline */}
              <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-300 to-transparent animate-pulse" />

              <span className="relative bg-gradient-to-b from-white via-white to-surface-400 bg-clip-text text-transparent drop-shadow-sm">
                ARENA AWAITS
              </span>
            </span>
          </h1>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-lg md:text-xl text-surface-300 max-w-2xl mx-auto mb-12 leading-relaxed"
        >
          Create or join a club. Compete with your community. Rivaled is where players come
          together, form rivalries, and prove their skill through talent-based matchmaking playing
          their favorite games.
        </motion.p>
      </div>

      <AnimatePresence>
        {showScroll && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ y: [0, 10, 0], opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{
              y: { repeat: Infinity, duration: 2 },
              opacity: { duration: 0.3 },
            }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
            onViewportLeave={() => setShowScroll(false)}
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-surface-500">
              Scroll to Scan
            </span>
            <div className="w-px h-12 bg-gradient-to-b from-brand-500 to-transparent" />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function ValuePropSection() {
  return (
    <section className="relative py-32 px-6 overflow-hidden">
      {/* Background: Diagonal gradient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-cyan-500/5 pointer-events-none" />

      {/* FIXED: Glows are now responsive.
         On Mobile: One at very top, one at very bottom.
         On Desktop: One left, one right. 
      */}
      <div className="absolute left-1/2 -translate-x-1/2 top-0 md:left-0 md:top-1/2 md:-translate-y-1/2 w-[600px] h-[350px] md:h-[600px] bg-brand-500/8 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute left-1/2 -translate-x-1/2 bottom-0 md:left-auto md:right-0 md:top-1/2 md:-translate-y-1/2 md:bottom-auto w-[600px] h-[350px] md:h-[600px] bg-cyan-500/8 blur-[160px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-20">
          <SectionHeader title="BUILT FOR COMPETITORS" subtitle="Why Use Rivaled" />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard
            icon={<Scale className="size-8 text-brand-400" />}
            title="Skill-Based Matching"
            desc="Advanced ELO system analyzes match data to ensure fair, balanced games. Play against opponents at your skill level for high-quality competitive matches."
          />
          <FeatureCard
            icon={<LayoutGrid className="size-8 text-purple-400" />}
            title="Independent Ranks"
            desc="Each queue has its own rank. Master 1v1 without grinding 5v5. Your 2v2 rating stays independent from your solo queue performance. Play what you love."
          />
          <FeatureCard
            icon={<Crown className="size-8 text-orange-400" />}
            title="Clubs"
            desc="Create your Club, recruit players, and compete together. Internal leaderboards, team stats, and club-exclusive queues help you build a lasting competitive community."
          />
        </div>
      </div>
    </section>
  );
}

function SupportedGames() {
  return (
    <section className="relative py-32 px-6 overflow-hidden">
      <GamesBackground />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="mb-14">
          <SectionHeader title="EVERY GAME. YOUR RULES." subtitle="Supported Titles" />
        </div>

        {/* Hero statement */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative text-center mb-14"
        >
          <div className="relative inline-block">
            <Gamepad2 className="size-16 md:size-20 text-brand-400 mx-auto mb-6 drop-shadow-[0_0_25px_rgba(31,251,221,0.4)]" />
            <span className="absolute -top-1 -right-1 flex h-5 w-5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-5 w-5 bg-brand-500 items-center justify-center text-[10px] font-bold text-surface-950">
                ∞
              </span>
            </span>
          </div>
          <h3 className="text-3xl md:text-5xl font-display font-bold text-white tracking-tight mb-4">
            IF YOU PLAY IT, <span className="text-brand-400">WE RANK IT</span>
          </h3>
          <p className="text-surface-400 text-lg max-w-xl mx-auto leading-relaxed">
            No game restrictions. Create a club, set up a queue for any title, and start competing
            with your own custom ranked ladder.
          </p>
        </motion.div>

        {/* Feature pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3"
        >
          {["Any game", "Any platform", "Any team size"].map((label) => (
            <div
              key={label}
              className="flex items-center gap-2 px-5 py-2.5 rounded-sm border border-brand-500/20 bg-brand-500/5 backdrop-blur-sm"
            >
              <div className="flex items-center justify-center size-5 rounded-md bg-brand-500/15 border border-brand-500/30 ">
                <Check className="size-3 text-brand-400" strokeWidth={3} />
              </div>
              <span className="text-sm font-medium text-brand-300 tracking-wide uppercase">
                {label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-32 px-6 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top border glow */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500 to-transparent" />

        {/* Centered radial gradient spotlight - teal and purple blend */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_50%,rgba(31,251,221,0.10),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(168,85,247,0.08),transparent_50%)]" />
      </div>

      <div className="relative max-w-4xl mx-auto text-center z-10">
        <div className="mb-8 flex justify-center">
          <div className="animate-float glow-champion">
            <RankIcon rank="champion" size="xxl" showLevels={false} />
          </div>
        </div>

        <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight mb-6">
          READY TO <span className="text-brand-400">COMPETE?</span>
        </h2>
        <p className="text-surface-300 text-lg max-w-xl mx-auto mb-10">
          Join thousands of players who've already found their perfect rivals. Your climb to
          Champion starts now.
        </p>

        <Link to="/sign-up">
          <Button
            variant="primary"
            size="lg"
            rightIcon={<ChevronRight className="size-6" />}
            className="px-10 py-5 text-lg shadow-[0_0_0px_rgba(31,251,221,0)] hover:scale-105 hover:shadow-[0_0_30px_rgba(31,251,221,0.3)] transition-all duration-300 ease-out"
          >
            Create Free Account
          </Button>
        </Link>
      </div>
    </section>
  );
}

function FeatureCard({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-surface-700/50 bg-surface-800/50 p-7 backdrop-blur-sm">
      <div className="flex flex-col items-center text-center">
        <div className="mb-6">
          <div className="flex items-center justify-center p-4 rounded-xl bg-gradient-to-b from-surface-800 to-surface-850 border border-surface-700/50 shadow-lg">
            {icon}
          </div>
        </div>

        <h3 className="mb-4 text-2xl font-display font-bold text-white uppercase tracking-wide">
          {title}
        </h3>
        <p className="text-surface-400 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center">
      <motion.span
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-brand-500 font-mono text-sm uppercase tracking-[0.3em] mb-4 block"
      >
        {subtitle}
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        className="text-4xl md:text-6xl font-display font-black text-white uppercase"
      >
        {title}
      </motion.h2>
      <div className="w-24 h-1 bg-brand-500 mx-auto mt-6 clip-path-slant-right" />
    </div>
  );
}
